import { defineStore } from 'pinia';
import { get, cloneDeep, findIndex, find, has, startsWith } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import { set, unset } from '@/helpers';
import NestedSet from '@/nested-set-lib';

import MenuApi from '@/store/api/cms/menu';
import pinia from '@/pinia';
import { useSetupStore, usePageStore } from '@/stores';

const defaultForm = {
    name: '',
    key: '',
    menuItems: []
};

const defaultRules = {
    menuItems: []
};

const defaultUi = {
    expandAll: true,
    form: {
        initialized: false,
        menuItems: []
    }
};

export const useMenuStore = defineStore('menu', {
    state: () => ({
        loading: true,
        options: {
            itemType: {
                loading: false,
                items: []
            },
        },
        validation: {
            usedPaths: []
        },
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        fixedPaths: () => ['/resetpassword']
    },

    actions: {
        setState({ path, value }) {
            set(this, path, value);
        },

        unsetState(path) {
            unset(this, path);
        },

        insertMenu(data) {
            let menuItems = get(data, 'menuItems') || [];

            // keep pending objects in menu items
            this.form.menuItems.forEach((item, i) => {
                if (item.pending) {
                    const idx = menuItems.findIndex(el => el.id === item.id);
                    if (idx >= 0) {
                        data.menuItems[idx].pending = item.pending;
                    }
                }
            });

            Vue.set(this, 'form', data);

            const validatedItems = NestedSet.validate(cloneDeep(data.menuItems));
            data.menuItems.map(el => {
                if (validatedItems.invalid.IDs.includes(el.id)) {
                    el._lft = 0;
                    el._rgt = 0;
                }
            });

            let validItems = data.menuItems.filter(el => validatedItems.valid.IDs.includes(el.id));
            const invalidItems = data.menuItems.filter(el => validatedItems.invalid.IDs.includes(el.id));

            let orphans = [];
            if (invalidItems.length) {
                data.menuItems = data.menuItems.sort((a, b) => b._rgt - a._rgt);

                let lft = data.menuItems[0]._rgt + 1;
                orphans = [
                    {
                        id: '__orphans',
                        key: '__orphans',
                        title: 'Orphans',
                        _lft: lft,
                        _rgt: undefined
                    }
                ];

                lft++;

                invalidItems.forEach(el => {
                    const orphan = {
                        id: el.id,
                        key: el.key,
                        title: el.title,
                        _lft: lft,
                        _rgt: lft + 1
                    };
                    orphans.push(orphan);
                    lft = lft + 2;
                });

                orphans[0]._rgt = lft;
            }

            menuItems = NestedSet.fix([...validItems, ...orphans]) || [];
            Vue.set(this.form, 'menuItems', menuItems);
        },

        updateItemTypeOptionsLoading(value) {
            Vue.set(this.options.itemType, 'loading', value);
        },

        insertItemTypeOptions(itemTypes) {
            Vue.set(this.options.itemType, 'items', itemTypes);
        },

        expandAll() {
            this.ui.expandAll = true;
        },

        collapseAll() {
            this.ui.expandAll = false;
        },

        updateLoading(value) {
            this.loading = value;
        },

        addUsedItemPathsToState(paths) {
            Vue.set(this.validation, 'usedPaths', [...paths, ...this.fixedPaths]);
        },

        // A store serves "/" through the menu item flagged as homepage - there is no
        // item with the path "/". Knowing whether one exists lets the new-page dialog
        // offer it instead of leaving a fresh store with an unreachable root.
        setHomepageExistsOutsideCurrentMenu(value) {
            Vue.set(this.validation, 'homepageExistsOutsideCurrentMenu', !!value);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormName(name) {
            Vue.set(this.form, 'name', name);
        },

        setFormRestriction(restriction) {
            Vue.set(this.form, 'restriction', restriction);
        },

        setFormMemberTags(memberTags) {
            Vue.set(this.form, 'memberTags', memberTags);
        },

        moveFormItem({ item, target, position }) {
            let items = cloneDeep(this.form.menuItems);
            item = cloneDeep(item);

            switch (position) {
                case 'before':
                    items = NestedSet.moveBefore(items, target, item);
                    break;
                case 'after':
                    items = NestedSet.moveAfter(items, target, item);
                    break;
                case 'inner':
                    items = NestedSet.moveInto(items, target, item);
                    break;
            }

            Vue.set(this.form, 'menuItems', items);
        },

        markItemAsHidden(item) {
            const index = findIndex(this.form.menuItems, { id: item.id });
            if (index >= 0) {
                Vue.set(this.form.menuItems[index], 'hidden', true);
                if (this.form.menuItems[index].pending) Vue.set(this.form.menuItems[index].pending, 'hidden', true);
            }
        },

        unmarkItemAsHidden(item) {
            const index = findIndex(this.form.menuItems, { id: item.id });
            if (index >= 0) {
                Vue.set(this.form.menuItems[index], 'hidden', false);
                if (this.form.menuItems[index].pending) Vue.set(this.form.menuItems[index].pending, 'hidden', false);
            }
        },

        insertPageToItem({ i, page }) {
            page = cloneDeep(page);
            delete page.sections;
            Vue.set(this.form.menuItems[i], 'page', page);
        },

        setSettingsValue({ i, key, value }) {
            const settings = get(this.form, `menuItems[${i}].settings`);

            if (!settings) {
                Vue.set(this.form.menuItems[i], 'settings', []);
                Vue.set(this.form.menuItems[i].settings, 0, { key, value });
            } else if (settings && settings.length === 0) {
                Vue.set(this.form.menuItems[i].settings, 0, { key, value });
            } else {
                const index = findIndex(settings, { key });
                if (index >= 0) {
                    Vue.set(this.form.menuItems[i].settings, index, { key, value });
                } else {
                    Vue.set(this.form.menuItems[i].settings, settings.length, { key, value });
                }
            }
        },

        setSettingsOptions({ i, key, options }) {
            const settings = get(this.form, `menuItems[${i}].settings`);
            if (!settings) {
                Vue.set(this.form.menuItems[i], 'settings', []);
                Vue.set(this.form.menuItems[i].settings, 0, { key, options });
            } else if (settings && settings.length === 0) {
                Vue.set(this.form.menuItems[i].settings, 0, { key, options });
            } else {
                const index = findIndex(settings, { key });
                if (index >= 0) {
                    Vue.set(this.form.menuItems[i].settings, index, { key, options });
                } else {
                    Vue.set(this.form.menuItems[i].settings, settings.length, { key, options });
                }
            }
        },

        addSettingsToMenuItem(data) {
            Vue.set(this.form.menuItems[data.index], 'settings', data.settings);
        },

        addTranslationToMenuItem(data) {
            const translations = get(this.form.menuItems[data.index], 'translations', []);
            let i = translations.findIndex(el => el.language.code === data.translation.language.code);
            if (i < 0) i = translations.length;
            Vue.set(this.form.menuItems[data.index].translations, i, data.translation);
        },

        removeTranslationFromMenuItem(data) {
            const translations = get(this.form.menuItems[data.index], 'translations', []);
            let i = translations.findIndex(el => el.language.code === data.translation.language.code);
            if (i >= 0) Vue.delete(this.form.menuItems[data.index].translations, i);
        },

        // Async actions
        fetchMenu({ key, cms }) {
            return MenuApi.fetchMenu(key, cms).then(menu => {
                this.insertMenu(cloneDeep(menu));
                this.resetRules();
                return menu;
            });
        },

        fetchMenuItem(id) {
            return MenuApi.fetchItem(id).then(item => {
                this.insertMenuItemData(cloneDeep(item));
                this.resetRules();
                return item;
            });
        },

        translateMenuItem({ menuItem, language, type }) {
            return MenuApi.translateMenuItem(menuItem, language, type);
        },

        save(menu) {
            if (!menu.id || startsWith(menu.id, 'new:')) {
                return this.create(menu);
            } else {
                return this.update(menu);
            }
        },

        async create(menu) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            menu = cloneDeep(menu);
            let items = get(menu, 'menuItems', []);
            items.forEach(item => {
                delete item.component;
                if (item.deleted) NestedSet.deleteItem(items, item);
            });

            const result = await MenuApi.create(menu);
            await usePageStore(pinia).fetchRestrictedAreaMemberTagOptions(cms.id);
            return result;
        },

        async update(menu) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            menu = cloneDeep(menu);
            let items = get(menu, 'menuItems', []);

            items.forEach((item, i) => {
                if (item.deleted) NestedSet.deleteItem(items, item);
                if (item.id === '__orphans') {
                    const children = NestedSet.getChildren(items, item);
                    children.forEach(el => {
                        delete el._lft;
                        delete el._rgt;
                    });
                    items.splice(i, 1);
                }
            });

            items.forEach(item => delete item.component);

            const result = await MenuApi.update(menu);
            await usePageStore(pinia).fetchRestrictedAreaMemberTagOptions(cms.id);
            return result;
        },

        fetchMenuItemPaths({ id, key }) {
            MenuApi.fetchItemPaths(id, key).then(items => {
                let paths = [];
                items.forEach(item => paths.push(item.path));
                this.addUsedItemPathsToState(paths);
                this.setHomepageExistsOutsideCurrentMenu(items.some(item => item.homepage));
            });
        },

        insertMenuItemData(data) {
            const i = findIndex(this.form.menuItems, item => item.id === data.id);
            if (i >= 0) {
                this.addSettingsToMenuItem({ index: i, settings: data.settings });
                usePageStore(pinia).insertPage(data.page);
            }
        }
    }
});
